import React from 'react';

import { Button } from './Button';

export interface SelectedValueDisplayProps {
  /** Pre-formatted text to display (e.g., "John Doe (12345)") */
  displayText: string;
  /** Callback when Change button is clicked */
  onChangeClick: () => void;
  /** Callback when Reset button is clicked */
  onResetClick: () => void;
  /** Optional error message to display below */
  error?: string;
  /** Optional test ID for testing */
  testId?: string;
  /** Disable the Change and Reset buttons */
  disabled?: boolean;
}

/**
 * SelectedValueDisplay - Displays a selected value with Change and Reset buttons
 *
 * A pure presentational component that shows a selected value as text with
 * action buttons to modify or clear the selection. Used in form contexts
 * where a value has been selected and needs to be displayed with edit controls.
 */
export const SelectedValueDisplay: React.FC<SelectedValueDisplayProps> = ({
  displayText,
  onChangeClick,
  onResetClick,
  error,
  testId,
  disabled = false,
}) => {
  return (
    <div data-testid={testId}>
      <div>
        {displayText}{' '}
        <div className="mt-4 flex flex-row gap-2">
          <Button
            variant="primary"
            size="md"
            onClick={onChangeClick}
            disabled={disabled}
            type="button"
          >
            Change
          </Button>
          <Button
            variant="tertiary"
            size="md"
            onClick={onResetClick}
            disabled={disabled}
            type="button"
          >
            Reset
          </Button>
        </div>
      </div>
      {error && <span className="text-xs text-red-800">{error}</span>}
    </div>
  );
};
